import { useState, useRef, useCallback, useEffect, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api, brandingAssetBlobUrl } from '@/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { Icon } from '@/components/icons';
import styles from './AdminBrandingPage.module.css';

interface BrandingData {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  portalTitle?: string | null;
  welcomeText?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  defaultLanguage?: string | null;
  logoObjectKey?: string | null;
}

type LogoFormat = 'PNG' | 'WEBP';

const LANGUAGES = ['en', 'fr'];
const DEFAULT_PRIMARY = '#0070F2';
const DEFAULT_SECONDARY = '#475569';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGO_MAX_BYTES = 512 * 1024;

/**
 * The file's bytes as base64, without the `data:` prefix.
 *
 * readAsDataURL rather than readAsArrayBuffer + manual encoding: the manual
 * route means a loop over a Uint8Array through String.fromCharCode, which
 * blows the call stack on files of exactly the size a logo can be.
 *
 * The prefix is stripped here even though core also strips it. Sending the
 * shorter payload is not the point — being explicit about what is on the wire
 * is, because the two sides are in different repositories and only one of them
 * gets read when this breaks.
 */
async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    // A read can fail on a file the user has since moved or a permissions
    // change mid-pick. Rejecting surfaces the upload error rather than
    // uploading an empty string.
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsDataURL(file);
  });

  const comma = dataUrl.indexOf(',');
  if (comma === -1) throw new Error('The file could not be read.');
  return dataUrl.slice(comma + 1);
}

export function AdminBrandingPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const packageInputRef = useRef<HTMLInputElement>(null);

  const { state, reload, replace } = useAsyncData<BrandingData>(
    `admin-branding-${institutionId}`,
    () => api.admin.branding.get(institutionId) as Promise<BrandingData>,
    !!institutionId,
  );

  const [form, setForm] = useState<BrandingData>({});
  const [dirty, setDirty] = useState<Partial<Record<keyof BrandingData, true>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  // The current logo as a blob URL (the asset route is authenticated, so an
  // <img> cannot load it directly). Set to null once the institution has none.
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Branding package import. The browser reads the folder's files as text and
  // POSTs them in one JSON body — multipart does not survive the accounts BFF.
  const [packageFiles, setPackageFiles] = useState<string[]>([]);
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageApplied, setPackageApplied] = useState(false);

  const setPreview = useCallback((url: string | null) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setLogoPreview(url);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  if (!formInitialized && state.status === 'ready' && state.data) {
    setForm({
      primaryColor: state.data.primaryColor ?? null,
      secondaryColor: state.data.secondaryColor ?? null,
      portalTitle: state.data.portalTitle ?? '',
      welcomeText: state.data.welcomeText ?? '',
      supportEmail: state.data.supportEmail ?? '',
      supportPhone: state.data.supportPhone ?? '',
      defaultLanguage: state.data.defaultLanguage ?? 'en',
      logoObjectKey: state.data.logoObjectKey ?? null,
    });
    setFormInitialized(true);
    if (state.data.logoObjectKey) {
      brandingAssetBlobUrl(institutionId, 'logo')
        .then((url) => {
          if (previewUrlRef.current !== url) setPreview(url);
        })
        .catch(() => {
          // no logo preview; the drop zone stays
        });
    }
  }

  const updateField = useCallback(<K extends keyof BrandingData>(key: K, value: BrandingData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => ({ ...prev, [key]: true }));
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!institutionId) return;
    const touched = Object.keys(dirty) as (keyof BrandingData)[];

    // The stored row is null when an institution never set a value, and the
    // resolved view falls back to the platform default. Writing the platform
    // default in as the institution's own value would freeze the rebrand and
    // block the platform from ever changing it — so untouched fields are never
    // sent, and an emptied field is sent as "unset".
    if (!touched.length) {
      setSaved(true);
      return;
    }

    const payload: Record<string, unknown> = {};
    for (const key of touched) {
      if (key === 'logoObjectKey') continue;
      const value = form[key];
      if (typeof value === 'string' && value.trim() === '') continue;
      payload[key] = value;
    }

    if (typeof payload.supportEmail === 'string' && !EMAIL_PATTERN.test(payload.supportEmail)) {
      setEmailInvalid(true);
      return;
    }
    setEmailInvalid(false);

    setSaving(true);
    setSaved(false);
    try {
      await api.admin.branding.update(institutionId, payload);
      replace(form);
      setDirty({});
      setSaved(true);
    } catch {
      setSaved(false);
    } finally {
      setSaving(false);
    }
  }, [institutionId, form, dirty, replace]);

  /**
   * Uploads a chosen file and records it as the logo.
   *
   * The bytes go THROUGH core, which stores them and answers with the object
   * key; the key and the measured dimensions are then recorded in a branding
   * update. This replaced a presigned PUT straight to MinIO — object storage is
   * now private to core's stack, so a presigned URL points at an address no
   * browser can resolve. Reads were already served by core, so the upload was
   * the only thing that had kept storage publicly exposed.
   *
   * The dimensions are still measured HERE, because only the browser has the
   * decoded image; core proves the byteSize against what it stored.
   */
  const handleLogoFile = useCallback(
    async (file: File) => {
      if (!institutionId) return;
      setLogoError(null);

      if (file.type !== 'image/png' && file.type !== 'image/webp') {
        setLogoError(t('admin.branding.logoInvalid'));
        return;
      }
      if (file.size > LOGO_MAX_BYTES) {
        setLogoError(t('admin.branding.logoInvalid'));
        return;
      }

      let widthPx = 0;
      let heightPx = 0;
      try {
        const url = URL.createObjectURL(file);
        const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
          image.onerror = reject;
          image.src = url;
        });
        URL.revokeObjectURL(url);
        widthPx = dimensions.width;
        heightPx = dimensions.height;
      } catch {
        setLogoError(t('admin.branding.logoInvalid'));
        return;
      }
      if (widthPx < 96 || heightPx < 24 || widthPx > 2048 || heightPx > 1024) {
        setLogoError(t('admin.branding.logoInvalid'));
        return;
      }

      const format: LogoFormat = file.type === 'image/webp' ? 'WEBP' : 'PNG';
      setLogoBusy(true);
      try {
        const dataBase64 = await fileToBase64(file);
        const ticket = await api.admin.branding.uploadAsset(institutionId, {
          kind: 'LOGO',
          format,
          dataBase64,
        });
        await api.admin.branding.update(institutionId, {
          logo: {
            objectKey: ticket.objectKey,
            format,
            widthPx,
            heightPx,
            byteSize: file.size,
          },
        });
        setPreview(URL.createObjectURL(file));
        setForm((prev) => ({ ...prev, logoObjectKey: ticket.objectKey }));
        setSaved(true);
      } catch {
        setLogoError(t('admin.branding.logoUploadError'));
      } finally {
        setLogoBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [institutionId, setPreview, t],
  );

  const removeLogo = useCallback(async () => {
    if (!institutionId || logoBusy) return;
    setLogoError(null);
    setLogoBusy(true);
    try {
      await api.admin.branding.update(institutionId, { logo: null });
      setPreview(null);
      setForm((prev) => ({ ...prev, logoObjectKey: null }));
      setSaved(true);
    } catch {
      setLogoError(t('admin.branding.logoUploadError'));
    } finally {
      setLogoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [institutionId, logoBusy, setPreview, t]);

  const readFileText = useCallback(
    (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
        reader.readAsText(file);
      }),
    [],
  );

  const handlePackageFolder = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPackageError(null);
    setPackageApplied(false);
    setPackageFiles(Array.from(files).map((file) => file.webkitRelativePath || file.name));
  }, []);

  /**
   * Reads the chosen folder and imports it as one branding package.
   *
   * The folder is whatever a brand or website exports: a design-tokens JSON
   * (the authority — core maps its colours to the palette roles), an optional
   * token stylesheet (stored as a branding asset and streamed like the logo),
   * and an optional markdown design description. The palette is graded by the
   * same contrast gates a manual edit is held to, so a refused package leaves
   * the current branding untouched.
   */
  const handlePackageImport = useCallback(async () => {
    if (!institutionId) return;
    const files = packageInputRef.current?.files;
    if (!files || files.length === 0) return;

    const list = Array.from(files);
    const jsonFile = list.find((file) => file.name.toLowerCase().endsWith('.json'));
    const cssFile = list.find((file) => file.name.toLowerCase().endsWith('.css'));
    const mdFile = list.find((file) => /\.(md|markdown)$/i.test(file.name));
    if (!jsonFile) {
      setPackageError(t('admin.branding.packageNoJson'));
      return;
    }

    setPackageBusy(true);
    setPackageError(null);
    try {
      const tokens = JSON.parse(await readFileText(jsonFile)) as Record<string, unknown>;
      const css = cssFile ? await readFileText(cssFile) : undefined;
      const markdown = mdFile ? await readFileText(mdFile) : undefined;
      const folderName = jsonFile.webkitRelativePath.split('/')[0] || undefined;

      const saved = await api.admin.branding.importPackage(institutionId, {
        packageName: folderName,
        tokens,
        css,
        markdown,
      });

      // The package replaces the palette (and the stylesheet) wholesale; the
      // rest of the form — title, texts, contact — is untouched by the import.
      setForm((prev) => ({
        ...prev,
        primaryColor: saved.primaryColor ?? null,
        secondaryColor: saved.secondaryColor ?? null,
      }));
      setDirty({});
      setSaved(false);
      setPackageApplied(true);
    } catch (err) {
      // A JSON.parse failure is a local SyntaxError; a refusal from core is
      // an ApiError — both refuse the import without touching the branding.
      setPackageError(
        err instanceof SyntaxError
          ? t('admin.branding.packageBadJson')
          : t('admin.branding.packageError'),
      );
    } finally {
      setPackageBusy(false);
      if (packageInputRef.current) packageInputRef.current.value = '';
    }
  }, [institutionId, readFileText, t]);

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.branding.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.branding.title')}</h1>
        <p className="page-subtitle">{t('admin.branding.subtitle')}</p>
      </div>

      {saved && <div className={styles.successMsg}>{t('admin.branding.saved')}</div>}
      {emailInvalid && <div className={styles.errorMsg}>{t('admin.branding.emailInvalid')}</div>}
      {logoError && <div className={styles.errorMsg}>{logoError}</div>}

      <Card className={styles.formCard}>
        <div className={styles.formGrid}>
          <FormField id="primaryColor" label={t('admin.branding.primaryColor')}>
            <div className={styles.colorInputRow}>
              <input
                id="primaryColor"
                type="color"
                className={styles.colorPickerInput}
                value={form.primaryColor || DEFAULT_PRIMARY}
                onChange={(e) => updateField('primaryColor', e.target.value)}
              />
              <span className={styles.colorSwatch} style={{ background: form.primaryColor || DEFAULT_PRIMARY }} />
            </div>
          </FormField>

          <FormField id="secondaryColor" label={t('admin.branding.secondaryColor')}>
            <div className={styles.colorInputRow}>
              <input
                id="secondaryColor"
                type="color"
                className={styles.colorPickerInput}
                value={form.secondaryColor || DEFAULT_SECONDARY}
                onChange={(e) => updateField('secondaryColor', e.target.value)}
              />
              <span className={styles.colorSwatch} style={{ background: form.secondaryColor || DEFAULT_SECONDARY }} />
            </div>
          </FormField>

          <FormField id="portalTitle" label={t('admin.branding.portalTitle')}>
            <input
              id="portalTitle"
              type="text"
              className={styles.textInput}
              placeholder={t('admin.branding.portalTitlePlaceholder')}
              value={form.portalTitle || ''}
              onChange={(e) => updateField('portalTitle', e.target.value)}
            />
          </FormField>

          <FormField id="defaultLanguage" label={t('admin.branding.defaultLanguage')}>
            <Select
              id="defaultLanguage"
              value={form.defaultLanguage || 'en'}
              onChange={(value) => updateField('defaultLanguage', value)}
              options={LANGUAGES.map((lang) => ({ value: lang, label: lang.toUpperCase() }))}
            />
          </FormField>

          <div className={styles.fullWidth}>
            <FormField id="welcomeText" label={t('admin.branding.welcomeText')}>
              <input
                id="welcomeText"
                type="text"
                className={styles.textInput}
                placeholder={t('admin.branding.welcomeTextPlaceholder')}
                value={form.welcomeText || ''}
                onChange={(e) => updateField('welcomeText', e.target.value)}
              />
            </FormField>
          </div>

          <FormField id="supportEmail" label={t('admin.branding.supportEmail')}>
            <input
              id="supportEmail"
              type="email"
              className={styles.textInput}
              placeholder={t('admin.branding.supportEmailPlaceholder')}
              value={form.supportEmail || ''}
              onChange={(e) => updateField('supportEmail', e.target.value)}
            />
          </FormField>

          <FormField id="supportPhone" label={t('admin.branding.supportPhone')}>
            <input
              id="supportPhone"
              type="tel"
              className={styles.textInput}
              placeholder={t('admin.branding.supportPhonePlaceholder')}
              value={form.supportPhone || ''}
              onChange={(e) => updateField('supportPhone', e.target.value)}
            />
          </FormField>

          <div className={styles.fullWidth}>
            <FormField id="logo" label={t('admin.branding.logo')}>
              {logoPreview ? (
                <div className={styles.logoPreview}>
                  <img className={styles.logoPreviewImg} src={logoPreview} alt="" />
                  <div className={styles.logoPreviewInfo}>
                    <p className={styles.logoPreviewName}>{t('admin.branding.logo')}</p>
                    <p className={styles.logoPreviewHint}>{t('admin.branding.logoHint')}</p>
                  </div>
                  <button type="button" className={styles.removeLogoBtn} onClick={removeLogo} disabled={logoBusy}>
                    <Icon name="close" size={16} />
                  </button>
                </div>
              ) : (
                <div
                  className={styles.dropZone}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                >
                  <Icon name="institution" size={32} className={styles.dropZoneIcon} />
                  <p className={styles.dropZoneText}>{logoBusy ? t('admin.branding.logoUploading') : t('admin.branding.logo')}</p>
                  <p className={styles.dropZoneHint}>{t('admin.branding.logoHint')}</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleLogoFile(file);
                }}
                className={styles.hiddenInput}
              />
            </FormField>
          </div>
        </div>
      </Card>

      <Card className={styles.formCard}>
        <h2 className={styles.packageTitle}>{t('admin.branding.packageTitle')}</h2>
        <p className={styles.packageHint}>{t('admin.branding.packageHint')}</p>
        {packageApplied && <div className={styles.successMsg}>{t('admin.branding.packageApplied')}</div>}
        {packageError && <div className={styles.errorMsg}>{packageError}</div>}
        <div
          className={styles.dropZone}
          onClick={() => packageInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') packageInputRef.current?.click(); }}
        >
          <Icon name="institution" size={32} className={styles.dropZoneIcon} />
          <p className={styles.dropZoneText}>{t('admin.branding.packagePick')}</p>
          {packageFiles.length > 0 && (
            <p className={styles.packageFileList}>{packageFiles.slice(0, 4).join(' · ')}</p>
          )}
        </div>
        <input
          ref={packageInputRef}
          type="file"
          multiple
          onChange={(e) => handlePackageFolder(e.target.files)}
          className={styles.hiddenInput}
          {...({ webkitdirectory: '' } as InputHTMLAttributes<HTMLInputElement>)}
        />
        <div className={styles.packageActions}>
          <Button
            onClick={handlePackageImport}
            loading={packageBusy}
            loadingLabel={t('admin.branding.packageImporting')}
            disabled={packageFiles.length === 0}
          >
            {t('admin.branding.packageImport')}
          </Button>
        </div>
      </Card>

      <Card className={styles.previewSection}>
        <h2 className={styles.previewTitle}>{t('admin.branding.title')}</h2>
        <div className={styles.previewCard}>
          <div
            className={styles.previewHeader}
            style={{ background: `linear-gradient(135deg, ${form.primaryColor || DEFAULT_PRIMARY}, ${form.secondaryColor || DEFAULT_SECONDARY})` }}
          >
            <div className={styles.previewLogo}>
              {logoPreview ? (
                <img className={styles.previewLogoImg} src={logoPreview} alt="" />
              ) : (
                <Icon name="institution" size={16} />
              )}
            </div>
            <div className={styles.previewHeaderText}>
              <p className={styles.previewHeaderTitle}>
                {form.portalTitle || t('admin.branding.portalTitle')}
              </p>
              <p className={styles.previewHeaderSub}>
                {activeContext?.institution?.name}
              </p>
            </div>
          </div>
          <div className={styles.previewBody}>
            <p className={styles.previewWelcome} style={{ color: form.primaryColor || DEFAULT_PRIMARY }}>
              {form.welcomeText || t('admin.branding.welcomeText')}
            </p>
            <p className={styles.previewWelcomeSub}>
              {form.supportEmail && `Email: ${form.supportEmail}`}
              {form.supportPhone && `${form.supportEmail ? ' · ' : ''}Phone: ${form.supportPhone}`}
            </p>
          </div>
        </div>
      </Card>

      <div className={styles.formActions}>
        <Button variant="secondary" onClick={reload}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} loading={saving} loadingLabel={t('common.loading')}>
          {t('admin.branding.save')}
        </Button>
      </div>
    </div>
  );
}